import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Chip,
  Divider,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Group as GroupIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PersonAdd as AddIcon,
  GroupAdd as GroupAddIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { createContact, searchContacts } from "../../redux/contactSlice";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

interface Contact {
  id: string;
  name: string;
  walletAddress?: string;
  email?: string;
  avatar?: string;
  isGroup?: boolean;
  lastMessage?: string;
  timestamp?: string;
  unread?: number;
  status?: "online" | "offline";
}

const ContactsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const { contacts, searchResults } = useAppSelector((state) => state.contacts);
  const [isEditing, setIsEditing] = useState(false);
  const [currentContact, setCurrentContact] = useState<Partial<Contact>>({
    name: "",
    walletAddress: "",
    email: "",
    isGroup: false,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dispatch = useAppDispatch();

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Trigger search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchContacts({ keyword: debouncedSearchTerm }));
    }
  }, [debouncedSearchTerm, dispatch]);

  const handleOpenAddForm = () => {
    setIsEditing(false);
    setCurrentContact({
      name: "",
      walletAddress: "",
      email: "",
      isGroup: false,
    });
    setShowForm(true);
  };

  const handleOpenEditForm = (contact: Contact) => {
    setIsEditing(true);
    setCurrentContact(contact);
    setEditId(contact.id);
    setShowForm(true);
  };

  const handleCloseForm = () => setShowForm(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentContact((prev) => ({
      ...prev,
      [name]: name === "isGroup" ? value === "true" : value,
    }));
  };

  const handleSaveContact = async () => {
    if (
      !currentContact.name ||
      (!currentContact.walletAddress && !currentContact.email)
    )
      return;

    await dispatch(
      createContact({
        name: currentContact.name || "",
        walletAddress: currentContact.walletAddress || "",
      })
    );

    handleCloseForm();
  };

  const handleDeleteContact = (id: string) => {
    // Delete contact logic
  };

  const handleAddContact = (contact: Contact) => {
    dispatch(
      createContact({
        name: contact.name,
        walletAddress: contact.walletAddress || "",
      })
    );
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "50vh" : "60vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.walletAddress
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      contact.emailAddress?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a search result is already in contacts
  const isContactAdded = (walletAddress?: string, email?: string) => {
    return contacts.some(
      (contact) =>
        contact.walletAddress === walletAddress ||
        contact.emailAddress === email
    );
  };

  // Generate a unique key for each contact
  const getContactKey = (contact: Contact) => {
    return `${contact.id || "new"}-${contact.walletAddress || ""}-${
      contact.email || ""
    }`;
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      {showForm ? (
        <Paper elevation={isMobile ? 0 : 3} sx={{ p: isMobile ? 2 : 3, mb: 2 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton
              onClick={handleCloseForm}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
            >
              {isEditing
                ? "Edit Contact"
                : currentContact.isGroup
                ? "Create New Group"
                : "Add New Contact"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={currentContact.name}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
              required
            />
            <TextField
              fullWidth
              label="Wallet Address"
              name="walletAddress"
              value={currentContact.walletAddress}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={currentContact.email}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
            />
            {!isEditing && (
              <TextField
                select
                fullWidth
                label="Type"
                name="isGroup"
                value={currentContact.isGroup ? "true" : "false"}
                onChange={handleInputChange}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="false">Contact</MenuItem>
                <MenuItem value="true">Group</MenuItem>
              </TextField>
            )}
            <TextField
              fullWidth
              label="Avatar URL (optional)"
              name="avatar"
              value={currentContact.avatar || ""}
              onChange={handleInputChange}
              size={isMobile ? "small" : "medium"}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={handleCloseForm}
              startIcon={<CloseIcon fontSize={isMobile ? "small" : "medium"} />}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveContact}
              color="primary"
              variant="contained"
              startIcon={<CheckIcon fontSize={isMobile ? "small" : "medium"} />}
              disabled={
                !currentContact.name ||
                (!currentContact.walletAddress && !currentContact.email)
              }
              size={isMobile ? "small" : "medium"}
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? 2 : 0}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
            >
              Contacts
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon fontSize={isMobile ? "small" : "medium"} />}
                onClick={handleOpenAddForm}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "Add" : "Add Contact"}
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  <GroupAddIcon fontSize={isMobile ? "small" : "medium"} />
                }
                onClick={() => {
                  setCurrentContact({
                    name: "",
                    walletAddress: "",
                    email: "",
                    isGroup: true,
                  });
                  setIsEditing(false);
                  setShowForm(true);
                }}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "Group" : "New Group"}
              </Button>
            </Box>
          </Box>
          <Box sx={{ p: isMobile ? 1 : 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Find user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
          </Box>

          <Paper elevation={isMobile ? 0 : 2} sx={scrollableBoxStyles}>
            <List>
              {/* Existing contacts section */}
              {filteredContacts.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ px: 2, pt: 1 }}>
                    Your Contacts
                  </Typography>
                  {filteredContacts.map((contact) => (
                    <React.Fragment key={getContactKey(contact)}>
                      <ListItem
                        secondaryAction={
                          <Box>
                            <IconButton
                              edge="end"
                              onClick={() => handleOpenEditForm(contact)}
                              size={isMobile ? "small" : "medium"}
                            >
                              <EditIcon
                                fontSize={isMobile ? "small" : "medium"}
                              />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteContact(contact.id)}
                              size={isMobile ? "small" : "medium"}
                            >
                              <DeleteIcon
                                fontSize={isMobile ? "small" : "medium"}
                              />
                            </IconButton>
                          </Box>
                        }
                        sx={{ py: isMobile ? 1 : 1.5 }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={contact.avatar}
                            sx={{ width: 40, height: 40 }}
                          >
                            {contact.isGroup ? (
                              <GroupIcon
                                fontSize={isMobile ? "small" : "medium"}
                              />
                            ) : (
                              <PersonIcon
                                fontSize={isMobile ? "small" : "medium"}
                              />
                            )}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              display="flex"
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: isMobile ? "0.875rem" : "1rem",
                                  mr: 1,
                                }}
                              >
                                {contact.name}
                              </Typography>
                              {contact.isGroup && (
                                <Chip
                                  label="Group"
                                  size="small"
                                  sx={{ mb: isMobile ? 0.5 : 0 }}
                                  color="secondary"
                                />
                              )}
                              {contact.status === "online" && (
                                <Chip
                                  label="Online"
                                  size="small"
                                  sx={{ ml: isMobile ? 0 : 1 }}
                                  color="success"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {contact.walletAddress && (
                                  <>
                                    {contact.walletAddress?.slice(0, 6) +
                                      "..." +
                                      contact.walletAddress?.slice(-4)}
                                  </>
                                )}
                              </Typography>
                              {contact.emailAddress && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <EmailIcon fontSize="inherit" />
                                  {contact.emailAddress}
                                </Typography>
                              )}
                            </>
                          }
                          sx={{ my: 0 }}
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </>
              )}

              {/* Search results section */}
              {searchQuery && searchResults && searchResults.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ px: 2, pt: 1 }}>
                    Search Results
                  </Typography>
                  {searchResults.map((result) => {
                    const isAdded = isContactAdded(
                      result.walletAddress,
                      result.emailAddress
                    );
                    return (
                      <React.Fragment key={getContactKey(result)}>
                        <ListItem
                          secondaryAction={
                            !isAdded && (
                              <IconButton
                                edge="end"
                                onClick={() => handleAddContact(result)}
                                size={isMobile ? "small" : "medium"}
                              >
                                <AddIcon
                                  fontSize={isMobile ? "small" : "medium"}
                                />
                              </IconButton>
                            )
                          }
                          sx={{ py: isMobile ? 1 : 1.5 }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={result.avatar}
                              sx={{ width: 40, height: 40 }}
                            >
                              <PersonIcon
                                fontSize={isMobile ? "small" : "medium"}
                              />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: isMobile ? "0.875rem" : "1rem",
                                    mr: 1,
                                  }}
                                >
                                  {result.name}
                                </Typography>
                                {isAdded && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    color="info"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                {result.walletAddress && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: isMobile
                                        ? "0.75rem"
                                        : "0.875rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {result.walletAddress?.slice(0, 6) +
                                      "..." +
                                      result.walletAddress?.slice(-4)}
                                  </Typography>
                                )}
                                {result.emailAddress && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: isMobile
                                        ? "0.75rem"
                                        : "0.875rem",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <EmailIcon fontSize="inherit" />
                                    {result.emailAddress}
                                  </Typography>
                                )}
                              </>
                            }
                            sx={{ my: 0 }}
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    );
                  })}
                </>
              )}

              {/* No results message */}
              {searchQuery &&
                filteredContacts.length === 0 &&
                (!searchResults || searchResults.length === 0) && (
                  <Typography
                    variant="body2"
                    sx={{ p: 2, textAlign: "center" }}
                  >
                    No contacts found matching "{searchQuery}"
                  </Typography>
                )}
            </List>
          </Paper>
        </>
      )}
    </Paper>
  );
};

export default ContactsSection;
